import { Box, Container, Link, Paper, Stack, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PolicyDocumentActions from './PolicyDocumentActions';

const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
};

const paragraphStyle = {
    display: 'block',
    marginTop: '10px',
    marginBottom: '10px',
};

const PrivacyPolicyComponent = () => {
    const contentRef = useRef<HTMLDivElement>(null);

    return (
        <Container maxWidth='lg' sx={{ mt: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, md: 5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                }}
            >
                <Stack spacing={3} ref={contentRef}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <Typography
                            variant='h1'
                            sx={{
                                textAlign: 'left',
                                textTransform: 'uppercase',
                            }}
                        >
                            Privacy Policy
                        </Typography>
                        <PolicyDocumentActions
                            contentRef={contentRef}
                            title='Privacy Policy'
                        />
                    </Box>
                    <Box>
                        <Typography variant='body3' sx={{ ...paragraphStyle }}>
                            Effective starting: September 20, 2023
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            What our policy covers
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Your privacy is important to us, and so is being
                            transparent about how we collect, use, and share
                            your personal information. This Privacy Policy
                            explains what information we collect, how we use it,
                            and your rights in relation to it.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            This policy applies when you use our products and
                            services, interact with us (such as attending an
                            event or visiting a property), or access our
                            platform in any way. If you do not agree with this
                            policy, please do not use our services or provide
                            your information.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            What information we collect about you
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            We collect information in the following ways:
                            <Stack component='ul' sx={paragraphStyle}>
                                <Typography variant='body3' component='li'>
                                    Account and Profile Information – Details
                                    you provide when registering, updating
                                    preferences, or adding users.
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Reservations – Information you submit or
                                    modify when making or updating a Reservation
                                    (Hosts and Guests only).
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Experience Listings (Hosts only) – Details
                                    provided when creating or editing an
                                    experience.
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Support & Enquiries – Data collected from
                                    support or website enquiries.
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Financial Information (Hosts only) – Bank
                                    account details for payment processing.
                                </Typography>
                            </Stack>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            How we use information we collect
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            We use your information to:
                            <Stack component='ul' sx={paragraphStyle}>
                                <Typography variant='body3' component='li'>
                                    Create and manage user accounts{' '}
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Process Reservations (Hosts and Guests only)
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Manage experience content and listings
                                    (Hosts only)
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Deliver customer support{' '}
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Send experience-related communications
                                    (Hosts and Guests only)
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Send Partner Service-related communications
                                    (Hosts & Contractors only)
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Process host and guest payments and maintain
                                    financial records
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Improve and personalise our platform{' '}
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Provide and enhance AI-powered features,
                                    including assisting with the host experience
                                    creation, Reservations, pricing
                                    recommendations, discounts, content
                                    generation, search functionality, customer
                                    support, and other platform services
                                </Typography>
                            </Stack>
                            We only share information with:
                            <Stack component='ul' sx={paragraphStyle}>
                                <Typography variant='body3' component='li'>
                                    Company admins (for support or reporting)
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Hosts and guests (when necessary for a
                                    Reservation)
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Registered users (for published experiences)
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Xero (for accounting and payment){' '}
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Amazon Web Services (AWS) and other cloud
                                    infrastructure providers used to host and
                                    operate the platform
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Artificial intelligence and technology
                                    service providers engaged by 7DAY to provide
                                    platform functionality, automation, content
                                    generation, analytics, and support services
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Internal team members for platform
                                    operations
                                </Typography>
                            </Stack>
                            We do not sell your information to third parties.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            Reviews and Testimonials
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Users may choose to submit reviews, ratings,
                            feedback, testimonials, photos, or other content
                            relating to Experiences and services available
                            through the platform.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            When submitting a review, users may have the option
                            to:
                            <Stack component='ul' sx={paragraphStyle}>
                                <Typography variant='body3' component='li'>
                                    Display the review publicly using their
                                    first name, last name initial, and profile
                                    photo;
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Display the review anonymously; or
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Keep the review private where such
                                    functionality is available.
                                </Typography>
                            </Stack>
                        </Typography>
                    </Box>
                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            How we store information we collect
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Our platform is hosted on Amazon Web Services (AWS),
                            a global cloud infrastructure provider with
                            industry-leading security and compliance standards.
                            Information is stored and processed using AWS
                            services and is protected through security measures
                            including encryption, access controls, monitoring,
                            logging, network segmentation, backup systems, and
                            protection against malicious activity. More
                            information about AWS security can be found at:{' '}
                            <Link
                                href='https://aws.amazon.com/security'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                https://aws.amazon.com/security
                            </Link>
                        </Typography>

                        <Typography variant='body3' sx={paragraphStyle}>
                            We also use Xero for accounting, invoicing, and
                            payment-related services. Financial information,
                            including bank account details provided by Hosts,
                            may be securely stored and processed through Xero in
                            accordance with applicable Australian privacy and
                            data protection laws.
                            <br />
                            Learn more:{' '}
                            <Link
                                href='https://www.xero.com/au/about/terms/privacy/'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                https://www.xero.com/au/about/terms/privacy/
                            </Link>
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            7DAY may also utilise artificial intelligence and
                            machine learning technologies, including services
                            provided by third-party providers such as OpenAI and
                            other AI service providers, to deliver certain
                            platform features and functionality. Information
                            submitted through the platform may be processed by
                            these services for the purpose of generating
                            content, assisting with platform operations,
                            improving user experiences, providing support, and
                            delivering AI-powered features. 7DAY only engages
                            providers that maintain appropriate security and
                            privacy standards.
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            Where third-party service providers are used, access
                            to information is limited to the extent reasonably
                            necessary for the provider to perform the relevant
                            services on behalf of 7DAY.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            Consent
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            By using our platform and submitting personal or
                            financial information, you consent to the
                            collection, storage, and use of your information as
                            outlined in this Privacy Policy.
                            <br />
                            When saving your bank account details, you agree
                            that:
                            <Stack component='ul' sx={paragraphStyle}>
                                <Typography variant='body3' component='li'>
                                    Your data will be securely stored in our
                                    platform and in Xero
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    It will be used only for payment processing
                                    and recordkeeping
                                </Typography>
                            </Stack>
                            You may withdraw consent at any time by contacting
                            us. Please note, removing your financial details may
                            impact your ability to receive payments.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            Your rights and access
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            You have the right to:
                            <Stack component='ul' sx={paragraphStyle}>
                                <Typography variant='body3' component='li'>
                                    Access and review your personal information
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Request corrections or updates{' '}
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Withdraw consent for data use{' '}
                                </Typography>
                                <Typography variant='body3' component='li'>
                                    Request deletion of data (where legally
                                    allowed)
                                </Typography>
                            </Stack>
                            To exercise any of these rights, contact us at{' '}
                            <Link href='mailto:support@7day.com.au'>
                                support@7day.com.au
                            </Link>{' '}
                            or{' '}
                            <Link component={RouterLink} to='/support'>
                                contact us here
                            </Link>
                            .
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            Changes to this policy
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            We may update this Privacy Policy from time to time.
                            When we do, we’ll update the “Effective” date at the
                            top of this page and notify users where required.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography gutterBottom sx={sectionTitleStyle}>
                            Contact us
                        </Typography>
                        <Typography variant='body3' sx={paragraphStyle}>
                            If you have any questions or concerns about this
                            Privacy Policy or how your data is handled, please
                            contact us:
                            <br />
                            📧{' '}
                            <Link href='mailto:support@7day.com.au'>
                                support@7day.com.au
                            </Link>
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        </Container>
    );
};

export default PrivacyPolicyComponent;
